import { useEffect, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import VideoLibrary from '@mui/icons-material/VideoLibrary';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { type ArtifactType } from '../../types/recordingTypes';
import { type RecordingDialogProps } from '../classroom/ui-types';
import { getRecordingArtifact, getRecordingInfo } from '../classroom/util';
import Loadable from '../common/loadable';

export const RecordingLinksDialog = (props: RecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, downloading, recording, onClose, userUuid, classUuid } = props;
    const [status, setStatus] = useState<'loading' | 'loaded'>('loading');
    const [artifacts, setArtifacts] = useState<Record<string, string>>({});
    const { data: schoolInfo } = useSchoolInfo();
    const [isLoadingArtifact, setIsLoadingArtifact] = useState(false);

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        if (schoolInfo?.uuid && userUuid && classUuid && recording?.recordingId) {
            const applyArtifacts = async () => {
                const info = await getRecordingInfo(schoolInfo.uuid!, userUuid, classUuid, recording?.recordingId);

                setArtifacts(info?.artifacts ?? {});
                setStatus('loaded');
            };

            setStatus('loading');
            applyArtifacts();
        } else {
            setArtifacts({});
            setStatus('loaded');
        }
    }, [classUuid, open, recording, schoolInfo?.uuid, userUuid]);

    const handleClickArtifact = async (artifactType: ArtifactType) => {
        if (!schoolInfo || !recording) {
            return;
        }
        setIsLoadingArtifact(true);
        const artifact = await getRecordingArtifact(
            schoolInfo.uuid,
            userUuid,
            classUuid,
            recording.recordingId,
            artifactType
        );
        if (!artifact) {
            throw new Error('Failure to get recording artifact');
        }
        setIsLoadingArtifact(false);
        (window as any).open(artifact.url, '_blank').focus();
    };

    return (
        <Dialog onClose={handleClose} open={open}>
            <Loadable isLoading={isLoadingArtifact}>
                <DialogTitle>
                    {t(downloading ? 'lti:recordings.downloadmedia' : 'lti:recordings.external-link')}
                    {status === 'loading' && <CircularProgress size={16} sx={{ marginLeft: '8px' }} />}
                </DialogTitle>

                <DialogContent>
                    {status === 'loaded' && (
                        <>
                            {Object.keys(artifacts).length > 0 ? (
                                <Stack gap={1}>
                                    {Object.entries(artifacts).map(([type]) => (
                                        <Box>
                                            <Button
                                                variant="text"
                                                key={type}
                                                onClick={() => {
                                                    handleClickArtifact(type as ArtifactType);
                                                }}
                                            >
                                                {downloading ? <DownloadIcon /> : <VideoLibrary />}

                                                <Typography ml={1}>{t(`lti:recordings.mediatypes.${type}`)}</Typography>
                                            </Button>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <DialogContentText>{t('lti:recordings.nomedia')}</DialogContentText>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button autoFocus onClick={() => handleClose()} variant="contained" color="primary">
                        {t('common:close')}
                    </Button>
                </DialogActions>
            </Loadable>
        </Dialog>
    );
};
